/**
 * テロップ描画。プレビュー（CanvasRenderingContext2D）と動画生成Worker
 * （OffscreenCanvasRenderingContext2D）が同じ実装を共有する。
 * 折り返し・行位置の計算は core に委譲し、ここは描画APIの呼び出しに徹する。
 */
import type { SubtitleSettings, SubtitleStyle } from '@/types';
import {
  wrapJapaneseText,
  computeLineCenters,
  subtitleFontString,
  type WrapResult,
} from '@/core/subtitleLayout';

// CanvasRenderingContext2D / OffscreenCanvasRenderingContext2D / node-canvas系の共通部分。
// DOMの型名を使わず構造だけで表現しているのは、サーバー側（DOM libなしのtsconfig）でも
// この描画コードをそのまま利用するため。
export interface Canvas2DContextLike {
  font: string;
  textAlign: string;
  textBaseline: string;
  fillStyle: string | object;
  strokeStyle: string | object;
  globalAlpha: number;
  lineWidth: number;
  lineJoin: string;
  miterLimit: number;
  measureText(text: string): { width: number };
  fillText(text: string, x: number, y: number): void;
  strokeText(text: string, x: number, y: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
}

/**
 * 文字間隔を考慮してテキストを描画
 */
export function drawTextWithLetterSpacing(
  ctx: Canvas2DContextLike,
  text: string,
  x: number,
  y: number,
  letterSpacing: number,
  isStroke: boolean
): void {
  if (letterSpacing === 0) {
    if (isStroke) {
      ctx.strokeText(text, x, y);
    } else {
      ctx.fillText(text, x, y);
    }
    return;
  }

  const chars = [...text];
  const totalWidth = ctx.measureText(text).width + letterSpacing * (chars.length - 1);
  let currentX = x - totalWidth / 2;

  chars.forEach((char, index) => {
    const charWidth = ctx.measureText(char).width;
    const charX = currentX + charWidth / 2;

    if (isStroke) {
      ctx.strokeText(char, charX, y);
    } else {
      ctx.fillText(char, charX, y);
    }

    currentX += charWidth + (index < chars.length - 1 ? letterSpacing : 0);
  });
}

/**
 * シーン単位で確定するテロップのレイアウト。
 * 折り返し（budouxパース）と行位置の計算は重いので、フレームごとではなく
 * これを一度だけ作り、フレーム描画では drawSubtitleLayout に渡して使い回す。
 */
export interface SubtitleLayout {
  wrapResult: WrapResult;
  lineCenters: number[];
  font: string;
}

/**
 * テロップのレイアウトを計算する（描画はしない）
 */
export function buildSubtitleLayout(
  ctx: Canvas2DContextLike,
  text: string,
  canvasWidth: number,
  canvasHeight: number,
  subtitleSettings: SubtitleSettings,
  style: SubtitleStyle
): SubtitleLayout {
  const font = subtitleFontString(style);

  if (!text.trim()) {
    return {
      wrapResult: { lines: [], exceedsMaxLines: false, actualLineCount: 0 },
      lineCenters: [],
      font,
    };
  }

  const maxWidth = canvasWidth - subtitleSettings.horizontalPadding * 2;

  ctx.font = font;

  const wrapResult = wrapJapaneseText(
    text,
    maxWidth,
    subtitleSettings.letterSpacing,
    (str) => ctx.measureText(str).width
  );

  const lineCenters = computeLineCenters(
    wrapResult.lines.length,
    style.fontSize,
    subtitleSettings.lineHeight,
    subtitleSettings.positionFromBottom,
    canvasHeight
  );

  return { wrapResult, lineCenters, font };
}

/**
 * 計算済みレイアウトに従ってテロップを描画する
 */
export function drawSubtitleLayout(
  ctx: Canvas2DContextLike,
  layout: SubtitleLayout,
  canvasWidth: number,
  subtitleSettings: SubtitleSettings,
  style: SubtitleStyle
): void {
  if (layout.wrapResult.lines.length === 0) return;

  ctx.font = layout.font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  layout.wrapResult.lines.forEach((line, index) => {
    const y = layout.lineCenters[index];
    const x = canvasWidth / 2;

    if (style.backgroundOpacity > 0) {
      const metrics = ctx.measureText(line);
      const bgPadding = 10;
      const bgWidth = metrics.width + bgPadding * 2 + subtitleSettings.letterSpacing * ([...line].length - 1);
      const bgHeight = style.fontSize + bgPadding;
      const bgX = x - bgWidth / 2;
      const bgY = y - bgHeight / 2;

      ctx.fillStyle = style.backgroundColor;
      ctx.globalAlpha = style.backgroundOpacity / 100;
      ctx.fillRect(bgX, bgY, bgWidth, bgHeight);
      ctx.globalAlpha = 1;
    }

    if (style.strokeWidth > 0) {
      ctx.strokeStyle = style.strokeColor;
      ctx.lineWidth = style.strokeWidth * 2;
      ctx.lineJoin = 'round';
      ctx.miterLimit = 2;
      drawTextWithLetterSpacing(ctx, line, x, y, subtitleSettings.letterSpacing, true);
    }

    ctx.fillStyle = style.textColor;
    drawTextWithLetterSpacing(ctx, line, x, y, subtitleSettings.letterSpacing, false);
  });
}

/**
 * Canvas上にテロップを描画（レイアウト計算と描画を一度に行う便宜関数）
 */
export function drawSubtitle(
  ctx: Canvas2DContextLike,
  text: string,
  canvasWidth: number,
  canvasHeight: number,
  subtitleSettings: SubtitleSettings,
  style: SubtitleStyle
): WrapResult {
  const layout = buildSubtitleLayout(ctx, text, canvasWidth, canvasHeight, subtitleSettings, style);
  drawSubtitleLayout(ctx, layout, canvasWidth, subtitleSettings, style);
  return layout.wrapResult;
}
